/**
 * Wishlist API Client
 * Handles all API communication for the wishlist system
 */

// Prevent duplicate class declaration if script is loaded multiple times
(function() {
  'use strict';
  
  // If WishlistAPI already exists, don't redeclare
  if (window.WishlistAPI && typeof window.WishlistAPI === 'object') {
    console.log('WishlistAPI already exists, skipping reinitialization');
    return;
  }

  // Prevent duplicate class declaration
  if (window.__WishlistAPILoading) {
    console.log('WishlistAPI is already loading, skipping');
    return;
  }
  
  window.__WishlistAPILoading = true;

  class WishlistAPI {
  constructor() {
    let apiUrl = window.wishlistConfig?.apiUrl || 'https://api.yourdomain.com/v1';
    // Ensure baseURL ends with /v1
    if (!apiUrl.endsWith('/v1')) {
      apiUrl = apiUrl.replace(/\/$/, '') + '/v1';
    }
    this.baseURL = apiUrl;
    this.token = this.getToken();
  }

  /**
   * Get auth token from storage
   */
  getToken() {
    return localStorage.getItem('wishlist_token') || sessionStorage.getItem('wishlist_token');
  }

  /**
   * Set auth token
   */
  setToken(token, persistent = false) {
    if (persistent) {
      localStorage.setItem('wishlist_token', token);
    } else {
      sessionStorage.setItem('wishlist_token', token);
    }
    this.token = token;
  }

  /**
   * Clear auth token
   */
  clearToken() {
    localStorage.removeItem('wishlist_token');
    sessionStorage.removeItem('wishlist_token');
    this.token = null;
  }

  /**
   * Make authenticated API request
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        this.clearToken();
        document.dispatchEvent(new CustomEvent('wishlist:auth:required'));
        throw new Error('Authentication required');
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        const errorMessage = error.error || error.message || `API error: ${response.status}`;
        console.error('API error response:', error);
        console.error('Request URL:', url);
        console.error('Request options:', JSON.stringify(options, null, 2));
        // Include validation errors if present
        if (error.errors && Array.isArray(error.errors)) {
          const validationErrors = error.errors.map(e => {
            const field = e.param || e.msg?.split(':')[0] || 'unknown';
            const message = e.msg || e.message || 'Invalid value';
            return `${field}: ${message}`;
          }).join(', ');
          throw new Error(`${errorMessage} - ${validationErrors}`);
        }
        throw new Error(errorMessage);
      }

      // Handle 204 No Content responses (e.g., DELETE requests)
      if (response.status === 204) {
        return null;
      }

      // Check if response has content before parsing JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        return text ? JSON.parse(text) : null;
      }
      
      return null;
    } catch (error) {
      console.error('Wishlist API error:', error);
      throw error;
    }
  }

  /**
   * Authentication
   */
  async login(shopifyCustomerId, email, name) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        shopifyCustomerId,
        email,
        name
      })
    });
    
    if (response.token) {
      this.setToken(response.token, true);
    }
    
    return response;
  }

  async sendMagicLink(email) {
    return this.request('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async verifyMagicLink(token, email) {
    const response = await this.request('/auth/verify-magic-link', {
      method: 'POST',
      body: JSON.stringify({ token, email })
    });
    
    if (response.token) {
      this.setToken(response.token, true);
    }
    
    return response;
  }

  async loginEmailPassword(email, password) {
    const response = await this.request('/auth/login-email-password', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    
    if (response.token) {
      this.setToken(response.token, true);
    }
    
    return response;
  }

  async register(email, password, name, company, isReserveMember) {
    const response = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, company, isReserveMember })
    });
    
    if (response.token) {
      this.setToken(response.token, true);
    }
    
    return response;
  }

  /**
   * Wishlists
   */
  async getWishlists(status = null, includeItems = false) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (includeItems) params.append('includeItems', 'true');
    
    const query = params.toString();
    return this.request(`/wishlists${query ? `?${query}` : ''}`);
  }

  async getWishlist(id, includeSubmission = true) {
    const params = new URLSearchParams();
    if (includeSubmission) params.append('includeSubmission', 'true');
    
    const query = params.toString();
    return this.request(`/wishlists/${id}${query ? `?${query}` : ''}`);
  }

  async createWishlist(data) {
    return this.request('/wishlists', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateWishlist(id, data) {
    return this.request(`/wishlists/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async duplicateWishlist(id) {
    return this.request(`/wishlists/${id}/duplicate`, {
      method: 'POST'
    });
  }

  async archiveWishlist(id, archived = true) {
    return this.request(`/wishlists/${id}/archive`, {
      method: 'PATCH',
      body: JSON.stringify({ archived })
    });
  }

  async deleteWishlist(id) {
    return this.request(`/wishlists/${id}`, {
      method: 'DELETE'
    });
  }

  /**
   * Last-used wishlist
   */
  async getLastUsedWishlist() {
    return this.request('/wishlists/last-used');
  }

  async setLastUsedWishlist(id) {
    return this.request(`/wishlists/${id}/set-last-used`, {
      method: 'POST'
    });
  }

  /**
   * Wishlist items
   */
  async addItem(wishlistId, itemData) {
    return this.request(`/wishlists/${wishlistId}/items`, {
      method: 'POST',
      body: JSON.stringify(itemData)
    });
  }

  async updateItem(wishlistId, itemId, updates) {
    return this.request(`/wishlists/${wishlistId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  }

  async removeItem(wishlistId, itemId) {
    return this.request(`/wishlists/${wishlistId}/items/${itemId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Submission
   */
  async submitWishlist(wishlistId, data) {
    // Extract customerInfo and event details
    const { name, email, company, phone, loadInDate, loadInTime, loadOutDate, loadOutTime, venueName, venueAddress, missingItems, projectNotes } = data;
    
    return this.request(`/wishlists/${wishlistId}/submit`, {
      method: 'POST',
      body: JSON.stringify({
        customerInfo: { name, email, company, phone },
        loadInDate,
        loadInTime,
        loadOutDate,
        loadOutTime,
        venueName,
        venueAddress,
        missingItems,
        projectNotes
      })
    });
  }

  /**
   * Get submission data for a wishlist
   */
  async getWishlistSubmission(wishlistId) {
    return this.request(`/wishlists/${wishlistId}/submission`);
  }

  /**
   * Client match
   */
  async getClientMatch() {
    return this.request('/users/me/client-match');
  }
}

  // Export singleton instance
  window.WishlistAPI = new WishlistAPI();
})();
